// This module provides stubs for datadog modules and ways to overwrite them
// when the actual datadog modules are available (in backend environments).
// This is useful for generating metrics in code shared by the frontend and backend,
// without generating errors in frontend environments.

/** Max length for DataDog custom metric tag values (conservative limit). */
export const DATADOG_TAG_VALUE_MAX_LEN = 200;

/**
 * Sanitize a string for use as the value part of a DataDog `key:value` tag.
 * Colons in values can confuse parsers; strip control chars and cap length.
 */
export function sanitizeDatadogTagValue(value: string): string {
  const noControls = value.replace(/[\x00-\x1f\x7f]/g, '_').replace(/:/g, '_');
  return noControls.length <= DATADOG_TAG_VALUE_MAX_LEN
    ? noControls
    : noControls.slice(0, DATADOG_TAG_VALUE_MAX_LEN);
}

/**
 * Add tags to the active span if one exists.
 * @param tags
 */
export function addTagsToActiveSpan(tags: DatadogTag) {
  const span = tracer.scope().active();
  if (span) {
    span.addTags(tags);
  }
}

/**
 * Async wrapper function for tracer.trace that adds a default service tag, captures thrown errors, and ensures done is called.
 * @param operation - The name of the operation.
 * Operation should be a hardcoded string, for example "cron".
 * This will also be used as the resource name if no resource is provided in the options.
 * @param options - The options for the trace. For example, { resource: \`cron.${taskName}\` } or {}
 * @param fn - The function to trace.
 * @returns The result of the function.
 */
export const traceAsync = async <T>(operation: string, options: DatadogTraceOptions, fn: DatadogTraceFn<Promise<T>>): Promise<T> => {
  return tracer.trace(operation, {...defaultTraceOptions, ...options}, async (span, done) => {
    try {
      const result = await fn(span, done);
      done();
      return result;
    } catch (e) {
      done(e instanceof Error ? e : new Error(String(e)));
      throw e;
    }
  });
}

/**
 * Wrapper function for tracer.trace that adds a default service tag and captures thrown errors.
 * For async functions, use `traceAsync` instead.
 * 
 * @param operation - The name of the operation.
 * Operation should be a hardcoded string, for example "cron".
 * This will also be used as the resource name if no resource is provided in the options.
 * @param options - The options for the trace. For example, { resource: \`cron.${taskName}\` } or {}
 * @param fn - The function to trace.
 * @returns The result of the function.
 */
export const traceSync = <T>(operation: string, options: DatadogTraceOptions, fn: DatadogTraceFn<T>): T =>  tracer.trace(operation, {...defaultTraceOptions, ...options}, fn);


/**
 * Wrapper function for sendDistributionMetric that ensures the function is defined, and does nothing if it is not.
 * This makes it safe to call this function even if the datadog agent is not installed.
 * @param name - The name of the metric.
 * @param value - The value of the metric.
 * @param tags - The tags of the metric, in the format of `key:value`.
 */
export function sendDistributionMetricSafe(name: string, value: number, ...tags: string[]) {
  try {
    _sendDistributionMetric(name, value, ...tags);
  } catch (e) {
    console.warn(`Error sending DD distribution metric: ${name} with value ${value} and tags ${tags.join(', ')}`, e);
  }
}

let defaultTraceOptions: DatadogTraceOptions = {};

export type DatadogTag = {[k: string]: string | number | DatadogTag};

// More at https://github.com/DataDog/dd-trace-java/blob/master/dd-trace-api/src/main/java/datadog/trace/api/DDSpanTypes.java
type DatadogSpanType = 'web' | 'serverless';

// https://datadoghq.dev/dd-trace-js/modules.html#TraceOptions
type DatadogTraceOptions = { service?: string, resource?: string, type?: DatadogSpanType }

type DatadogTraceFn<T> = (span: DatadogSpan, done: (error?: Error) => void) => T;

export type DatadogSpan = {
  setTag: (key: string, value: string | number | DatadogTag) => void,
  addTags: (tags: DatadogTag) => void
}

export let tracer = new (class {
  /**
   * @deprecated
   * Use the wrapper function `traceSync` or `traceAsync` instead.
   * (`import { traceSync, traceAsync } from "@aidkitorg/types/lib/datadog"`)
   * 
   * https://datadoghq.dev/dd-trace-js/interfaces/export_.Tracer.html#trace
   * @param name resource name
   * @param fn 
   */
  trace<T extends DatadogTraceFn<any>>(name: string, fn: T): ReturnType<T>;
  trace<T extends DatadogTraceFn<any>>(name: string, options: DatadogTraceOptions, fn: T): ReturnType<T>;
  trace<T extends DatadogTraceFn<any>>(arg0: string, arg1: T | DatadogTraceOptions, arg2?: T) {
    if (arg2) return arg2({
      setTag: ()=>{},
      addTags: ()=>{},
    }, ()=>{})
    if (typeof arg1 === "function") return arg1({
      setTag: ()=>{},
      addTags: ()=>{},
    }, ()=>{})
  }
  /** @deprecated
   * You can use `addTagsToActiveSpan` instead for convenience and code simplicity.
   */
  scope() { return ({
    active: (): DatadogSpan | null => null
  })}
  use(plugin: string, config: {
    headers: string[]
  }) {}
})

export function setTypesPackageTracer(_tracer: any, _defaultTraceOptions: DatadogTraceOptions) {
  tracer = _tracer;
  defaultTraceOptions = _defaultTraceOptions
}

let _sendDistributionMetric: (name: string, value: number, ...tags: string[]) => void = () => {};
export function setSendDistributionMetric(sendDistributionMetric: (name: string, value: number, ...tags: string[]) => void) {
  _sendDistributionMetric = sendDistributionMetric;
}
